import React, { useEffect, useState } from "react";

import { RadioGroup, Radio, FormControl } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTableOne,
  fetchTableTwo,
  fetchTableThree,
  fetchDayData,
  fetchDayByCategory,
  toggleLiverun,
  setSelectedDate,
  setSelectedDay,
} from "../reducers/dashFrameSlice";

const COLORS = {
  filed: "#81c784",
  filed_abnormal: "#ffb74d",
  not_filed_range: "#e57373",
  not_filed_setup: "#64b5f6",
  not_filed_already: "#ba68c8",
};

const BCOLORS = {
  filed: "#388e3c",
  filed_abnormal: "#f57c00",
  not_filed_range: "#c62828",
  not_filed_setup: "#1565c0",
  not_filed_already: "#6a1b9a",
};

const COLOR_TITLES = {
  filed: "Filed Normal",
  filed_abnormal: "Not Filed (Inconclusive Report)",
  not_filed_range: "Not Filed (Results out of range)",
  not_filed_setup: "Not Filed (Rules not setup)",
  not_filed_already: "Not Filed (Already filed by clinician)",
};

const TABLE_THREE_COLUMNS = [
  {
    field: "output_date",
    headerName: "Date",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pathology_enabled",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total Enabled <br /> Path Cat
      </div>
    ),
  },
  {
    field: "enabled_received",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Enabled Path <br /> Reports Recvd
      </div>
    ),
  },
  {
    field: "filed",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Total <br /> Filed
      </div>
    ),
  },
  {
    field: "per_filed",
    flex: 1,
    cellClassName: "bg-green",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        % Total <br /> Filed
      </div>
    ),
  },
  {
    field: "out_of_range",
    flex: 1,
    cellClassName: "bg-orange",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Out of Range <br /> Reviewed
      </div>
    ),
  },
  {
    field: "per_out_of_range",
    flex: 1,
    cellClassName: "bg-orange",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        % Out of Range <br /> Reviewed
      </div>
    ),
  },
  {
    field: "filed_by_clinician",
    flex: 1,
    cellClassName: "bg-pink",
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Already Filed <br /> by GP
      </div>
    ),
  },
  {
    field: "not_setup_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Path Cat Recvd <br /> Not Enabled
      </div>
    ),
  },
  {
    field: "inreview",
    flex: 1,
    align: "center",
    headerAlign: "center",
    headerName: "In Review",
  },
];

const TABLE_CATEGORY_COLUMNS = [
  {
    field: "category",
    headerName: "Category",
    flex: 3,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "filed",
    headerName: "Filed",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-green",
  },
  {
    field: "filed_abnormal",
    headerName: "Filed Abnormal",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-green",
  },
  {
    field: "not_filed_range",
    headerName: "Not Filed (Range)",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-orange",
  },
  {
    field: "not_filed_setup",
    headerName: "Not Filed (Setup)",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-orange",
  },
  {
    field: "not_filed_already",
    headerName: "Not Filed (Already)",
    flex: 1,
    align: "center",
    headerAlign: "center",
    cellClassName: "bg-pink",
  },
];

const TABLE_DAYDATA_COLUMNS = [
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "category",
    headerName: "Category",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "endtime",
    headerName: "End Time",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "notes",
    headerName: "Notes",
    flex: 4,
    align: "center",
    headerAlign: "center",
  },
];

const PIE_COLORS = [
  "#81c784",
  "#4db6ac",
  "#ffd54f",
  "#9575cd",
  "#7986cb",
  "#a1887f",
  "#f06292",
  "#64b5f6",
  "#e57373",
  "#ba68c8",
];

const MessageFrame = () => {
  const dispatch = useDispatch();
  const {
    liverun,
    tableOne,
    tableOneTotalProcessed,
    tableOneInreviewCategories,
    tableOneEnabledCategories,
    tableOneMonthlyTrend,

    tableTwo,
    tableThree,
    tableDayByCategory,
    tableDayData,
    selectedDate,
    selectedDay,
    loadingOne,
    loadingTwo,
    loadingDayByCategory,
    loadingDayData,
  } = useSelector((state) => state.dashFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [visibleKeys, setVisibleKeys] = useState({
    filed: true,
    filed_abnormal: false,
    not_filed_range: false,
    not_filed_setup: false,
    not_filed_already: false,
  });

  const [page, setPage] = useState(0);
  const [page3, setPage3] = useState(0);
  const [page4, setPage4] = useState(0);
  const [selectedChartType, setSelectedChartType] = useState("filed");

  const pageSize = 10;
  const totalPages = Math.ceil(tableTwo.length / pageSize);

  const paginatedData = tableTwo
    .filter((entry) => entry.filed > 0)
    .sort((a, b) => b.filed - a.filed)
    .slice(page * pageSize, (page + 1) * pageSize);

  const paginatedData3 = tableTwo
    .filter((entry) => entry.not_filed_range > 0)
    .sort((a, b) => b.not_filed_range - a.not_filed_range)
    .slice(page3 * pageSize, (page3 + 1) * pageSize);

  const paginatedData4 = tableTwo
    .filter((entry) => entry.not_filed_setup > 0)
    .sort((a, b) => b.not_filed_setup - a.not_filed_setup)
    .slice(page4 * pageSize, (page4 + 1) * pageSize);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchTableOne({ liverun, license_key }));
    }
  }, [liverun, license_key, dispatch]);

  useEffect(() => {
    //select the first bar
    if (tableOne.length > 0 && license_key && !selectedDate) {
      const firstDate = tableOne[0].date;
      const { start_date, end_date } = tableOne[0];
      dispatch(setSelectedDate(firstDate));
      dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
      dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
      setPage(0);
      setPage3(0);
      setPage4(0);
    }
  }, [tableOne, license_key, liverun, dispatch, selectedDate]);

  const handleChartClick = (data) => {
    const dateTitle = data?.activeLabel;
    if (!dateTitle) return;
    const matchedRecord = tableOne.find((entry) => entry.date === dateTitle);
    if (!matchedRecord) return;
    const { start_date, end_date } = matchedRecord;
    dispatch(setSelectedDate(dateTitle));
    dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
    dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
    setPage(0);
    setPage3(0);
    setPage4(0);
  };

  const handleRowSelect = (params) => {
    const dateTitle = params.row?.output_date;
    dispatch(setSelectedDay(dateTitle));
    dispatch(fetchDayByCategory({ date: dateTitle, liverun, license_key }));
    dispatch(fetchDayData({ date: dateTitle, liverun, license_key }));
  };

  const chartFont = { fontSize: 11 };

  const pieData =
    selectedChartType === "filed"
      ? paginatedData
      : selectedChartType === "not_filed_range"
        ? paginatedData3
        : paginatedData4;

  const renderLegend = ({ payload }) => {
    const sorted = [...payload].sort(
      (a, b) => b.payload[selectedChartType] - a.payload[selectedChartType],
    );

    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {sorted.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <span style={{ color: entry.color }}>
              {entry.payload.category} : {entry.payload[selectedChartType]}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Box sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
      <Paper elevation={2} sx={{ mt: 1, p: 2 }}>
        <Grid
          container
          spacing={2}
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
          }}
        >
          <Grid item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography variant="h4">{tableOneTotalProcessed}</Typography>
              <Typography variant="body2">
                Total Pathology
                <br />
                Reports Filed
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography variant="h4">
                {Math.round((tableOneTotalProcessed / (60 * 24)) * 2) / 2} days
              </Typography>
              <Typography variant="body2">
                Time Saved
                <br />
                <Box component="span" fontSize={12}>
                  (based on 1min per filing)
                </Box>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography variant="h4">{tableOneEnabledCategories}</Typography>
              <Typography variant="body2">
                Total Pathology
                <br />
                Categories Enabled
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography variant="h4">{tableOneInreviewCategories}</Typography>
              <Typography variant="body2">
                Total Pathology
                <br />
                Categories Inreview
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Box
              sx={{
                textAlign: "center",
                height: "100%",
              }}
            >
              <Typography fontWeight="bold">Monthly Trend</Typography>
              {tableOneMonthlyTrend.map((item, idx) => {
                const isPositive = item.percent_change_from_prev > 0;
                const arrow = isPositive ? "▲" : "▼";
                const arrowColor = isPositive ? "lightgreen" : "red";

                return (
                  <Typography key={idx} variant="body2">
                    {item.month}: {item.filed} &nbsp;
                    <Box component="span" sx={{ color: arrowColor }}>
                      {arrow} {Math.abs(item.percent_change_from_prev)}%
                    </Box>
                  </Typography>
                );
              })}
            </Box>
          </Grid>
        </Grid>

        {/* Top Controls */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Activity - {liverun ? "Live Data" : "Dry Run Data"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={liverun}
                onChange={() => dispatch(toggleLiverun())}
              />
            }
            label="Live / Dry Run"
          />
        </Box>

        {/* Color Legend */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {Object.entries(COLORS).map(([key, color]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  size="small"
                  checked={visibleKeys[key]}
                  onChange={() =>
                    setVisibleKeys((prev) => ({
                      ...prev,
                      [key]: !prev[key],
                    }))
                  }
                  sx={{
                    color,
                    "&.Mui-checked": { color },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color }}>
                  {COLOR_TITLES[key]}
                </Typography>
              }
            />
          ))}
        </Box>

        {/* Charts Side-by-Side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* Left Column: Date-wise Filings */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">Weekly Filings</Typography>
              {/* Optional: Chart-specific switches can go here if needed */}
            </Box>
            {loadingOne ? (
              <CircularProgress />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={tableOne}
                  margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                  onClick={handleChartClick}
                >
                  <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                  <XAxis dataKey="date" style={chartFont} />
                  <YAxis style={chartFont} />
                  <Tooltip contentStyle={chartFont} />
                  <Legend wrapperStyle={chartFont} />
                  {visibleKeys.filed && (
                    <Bar
                      dataKey="filed"
                      name="Filed Normal"
                      stackId="a"
                      fill={COLORS.filed}
                      stroke={BCOLORS.filed}
                      strokeWidth={1}
                    />
                  )}
                  {visibleKeys.filed_abnormal && (
                    <Bar
                      dataKey="filed_abnormal"
                      name="Not Filed (Inconclusive Report)"
                      stackId="a"
                      fill={COLORS.filed_abnormal}
                      stroke={BCOLORS.filed_abnormal}
                      strokeWidth={1}
                    />
                  )}
                  {visibleKeys.not_filed_range && (
                    <Bar
                      dataKey="not_filed_range"
                      name="Not Filed (Results out of range)"
                      stackId="a"
                      fill={COLORS.not_filed_range}
                      stroke={BCOLORS.not_filed_range}
                      strokeWidth={1}
                    />
                  )}
                  {visibleKeys.not_filed_setup && (
                    <Bar
                      dataKey="not_filed_setup"
                      name="Not Filed (Rules not setup)"
                      stackId="a"
                      fill={COLORS.not_filed_setup}
                      stroke={BCOLORS.not_filed_setup}
                      strokeWidth={1}
                    />
                  )}
                  {visibleKeys.not_filed_already && (
                    <Bar
                      dataKey="not_filed_already"
                      name="Not Filed (Already filed by clinician)"
                      stackId="a"
                      fill={COLORS.not_filed_already}
                      stroke={BCOLORS.not_filed_already}
                      strokeWidth={1}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          {/* Right Column: Category Breakdown */}
          {selectedDate && !loadingTwo && (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    mr: 1,
                    whiteSpace: "nowrap",
                    minWidth: 100,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "bold",
                  }}
                >
                  {selectedDate}
                </Typography>
                <FormControl component="fieldset" sx={{ ml: 0.5 }}>
                  <RadioGroup
                    row
                    value={selectedChartType}
                    onChange={(e) => {
                      setSelectedChartType(e.target.value);
                      setPage(0); // Reset page
                    }}
                  >
                    <FormControlLabel
                      value="filed"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">Filed Normal</Typography>
                      }
                    />
                    <FormControlLabel
                      value="not_filed_range"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Not Filed (Results out of range)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="not_filed_setup"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Not Filed (Rules not setup)
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Tooltip contentStyle={chartFont} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="left"
                    content={renderLegend}
                    wrapperStyle={{
                      ...chartFont,
                      lineHeight: "24px",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <Pie
                    data={pieData}
                    dataKey={selectedChartType}
                    nameKey="category"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    label
                    stroke="#fff"
                    strokeWidth={1}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    mt: -5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page === 0
                        : selectedChartType === "not_filed_range"
                          ? page3 === 0
                          : page4 === 0
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p - 1)
                        : selectedChartType === "not_filed_range"
                          ? setPage3((p) => p - 1)
                          : setPage4((p) => p - 1);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "filed"
                        ? page >= totalPages - 1
                        : selectedChartType === "not_filed_range"
                          ? page3 >= totalPages - 1
                          : page4 >= totalPages - 1
                    }
                    onClick={() => {
                      selectedChartType === "filed"
                        ? setPage((p) => p + 1)
                        : selectedChartType === "not_filed_range"
                          ? setPage3((p) => p + 1)
                          : setPage4((p) => p + 1);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
          {selectedDate && !loadingTwo && (
            <DataGrid
              rowHeight={25}
              rows={tableThree}
              columns={TABLE_THREE_COLUMNS}
              pagination={false} // disable pagination
              hideFooter={true}
              onRowClick={handleRowSelect}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  marginBottom: "20px", // space between header and first row
                },
              }}
            />
          )}
        </Box>

        {selectedDay && (
          <>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Category Data for {selectedDay}
              </Typography>
              {loadingDayByCategory ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={tableDayByCategory}
                  columns={TABLE_CATEGORY_COLUMNS}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              )}
            </Box>

            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Day Data for {selectedDay}
              </Typography>
              {loadingDayData ? (
                <CircularProgress />
              ) : (
                <DataGrid
                  rowHeight={25}
                  rows={tableDayData}
                  columns={TABLE_DAYDATA_COLUMNS}
                  pageSize={5}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MessageFrame;
